import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Loader2, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const SectorRanking = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/rh/ranking-sectors`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
  );

  const ranking = data?.ranking || [];
  const month = data?.month || '';
  const monthLabel = month ? new Date(month + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) : '';

  const getMedalColor = (pos) => {
    if (pos === 1) return 'text-yellow-500';
    if (pos === 2) return 'text-slate-400';
    if (pos === 3) return 'text-amber-700';
    return 'text-slate-300';
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600 bg-emerald-50';
    if (score >= 40) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Ranking de Engajamento
        </h1>
        <p className="text-slate-600 mt-1 capitalize">{monthLabel}</p>
      </div>

      {ranking.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Sem dados de ranking</h3>
            <p className="text-slate-500">Cadastre colaboradores com departamentos para ver o ranking.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top 3 cards */}
          {ranking.length >= 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {ranking.slice(0, 3).map((r, i) => (
                <Card key={r.department} className={`border-slate-100 ${i === 0 ? 'ring-2 ring-yellow-200' : ''}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-3xl font-bold ${getMedalColor(r.position)}`} style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {r.position}°
                      </span>
                      <span className={`text-sm font-bold px-3 py-1 rounded-full ${getScoreColor(r.engagement_score)}`}>
                        {r.engagement_score} pts
                      </span>
                    </div>
                    <h3 className="font-bold text-slate-900 text-lg mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{r.department}</h3>
                    <div className="space-y-1 text-sm text-slate-500">
                      <div className="flex justify-between">
                        <span>Colaboradores ativos</span>
                        <span className="font-semibold text-slate-700">{r.active_students}/{r.total_students}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Check-ins no mês</span>
                        <span className="font-semibold text-slate-700">{r.total_checkins}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Adesão</span>
                        <span className="font-semibold text-slate-700">{r.adherence}%</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Progress value={r.engagement_score} className="h-2 bg-slate-100" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Full ranking table */}
          <Card className="border-slate-100" data-testid="ranking-table">
            <CardHeader>
              <CardTitle className="text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Ranking Completo
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Pos</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Setor</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Colaboradores</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Ativos</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Check-ins</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Média</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Adesão</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {ranking.map(r => (
                    <tr key={r.department} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-5 py-3">
                        <span className={`font-bold ${getMedalColor(r.position)}`}>{r.position}°</span>
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-900">{r.department}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{r.total_students}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{r.active_students}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{r.total_checkins}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{r.avg_checkins}/mês</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{r.adherence}%</td>
                      <td className="px-5 py-3">
                        <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${getScoreColor(r.engagement_score)}`}>
                          {r.engagement_score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// HR Integration Component

export default SectorRanking;
